import { MarkEmailRead, Warning } from '@mui/icons-material';
import {
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ModalV2 from '/@/components/bayer/modal-v2';
import ChipLabel from '/@/components/chip-label';
import type { parseUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';

const tableBorderSx = { border: '1px solid #E1E1E1' };
const tableLayoutSx = { tableLayout: 'fixed' as const, width: '100%' };

export { tableBorderSx, tableLayoutSx };

export type UserNodeWithSubtype = ReturnType<typeof parseUserNode> & {
    subtype?: 'common' | 'premium';
    parents?: number[];
};

export interface InvitationResultItem {
    userNode: UserNodeWithSubtype;
    status: 'success' | 'error';
    errorMessage?: string;
}

const COLUMN_WIDTHS = {
    productor: '35%',
    advisorSegment: '20%',
    status: '45%',
} as const;

const GrowerAndAdvisorCells = ({
    userNode,
    showSegmentoColumn,
    advisorName,
}: {
    userNode: UserNodeWithSubtype;
    showSegmentoColumn: boolean;
    advisorName?: string;
}) => {
    const segmentType = userNode.subtype === 'common' ? 'consultant' : 'rv';
    return (
        <>
            <TableCell sx={{ width: COLUMN_WIDTHS.productor }}>
                <Stack spacing={0.5}>
                    <Typography variant='body1' color='primary'>
                        {userNode.name}
                    </Typography>
                    {userNode.location && (
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {userNode.location}
                        </Typography>
                    )}
                </Stack>
            </TableCell>
            <TableCell sx={{ width: COLUMN_WIDTHS.advisorSegment }}>
                {showSegmentoColumn ? (
                    <ChipLabel label={userNode.subtype === 'common' ? _('Otros') : _('Premium')} type={segmentType} />
                ) : advisorName && advisorName !== '-' ? (
                    <ChipLabel label={advisorName} type='advisor' />
                ) : (
                    <ChipLabel label={_('Sin asignar')} type='gray' />
                )}
            </TableCell>
        </>
    );
};

const StatusCell = ({ item }: { item: InvitationResultItem }) => {
    if (item.status === 'success') {
        return (
            <TableCell sx={{ width: COLUMN_WIDTHS.status }}>
                <Stack direction='row' alignItems='center' spacing={1}>
                    <MarkEmailRead sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant='body2' color='primary'>
                        {_('La solicitud se envió correctamente a {{email}}', { email: item.userNode.email || '' })}
                    </Typography>
                </Stack>
            </TableCell>
        );
    }
    return (
        <TableCell sx={{ width: COLUMN_WIDTHS.status }}>
            <Stack direction='row' alignItems='center' spacing={1}>
                <Warning sx={{ color: 'warning.main', fontSize: 20 }} />
                <Typography variant='body2' color='primary'>
                    {item.errorMessage ?? _('Este productor ya compartió su operación con otro usuario')}
                </Typography>
            </Stack>
        </TableCell>
    );
};

const InvitationResultsModal = ({
    open,
    onClose,
    results,
    showSegmentoColumn,
    advisorNameByNodeId,
}: {
    open: boolean;
    onClose: () => void;
    results: InvitationResultItem[];
    showSegmentoColumn: boolean;
    advisorNameByNodeId: Record<number, string>;
}) => {
    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={_('Solicitar a los productores acceder a sus lotes de FieldView')}
            actions={
                <Button variant='contained' color='secondary' onClick={onClose}>
                    {_('Entendido')}
                </Button>
            }
            bodyProps={{
                sx: { overflow: 'auto', maxHeight: { xs: '80vh' }, textAlign: 'start' },
                spacing: 3,
            }}
            actionsProps={{
                justifyContent: 'flex-end',
            }}
            maxWidth='md'
        >
            <Stack className='mp-mask'>
                <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                    {_('Enviamos un mail para que el productor pueda habilitarte el acceso a sus lotes.')}
                </Typography>
                <Typography variant='subtitle1' color='primary'>
                    {_(
                        'Si alguna solicitud no se procesó, es porque esos lotes ya están compartidos con otro usuario. Deberás coordinar con el productor para obtener acceso.'
                    )}
                </Typography>
            </Stack>

            <TableContainer sx={tableBorderSx}>
                <Table sx={tableLayoutSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: COLUMN_WIDTHS.productor }}>
                                <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                    {_('Productor')}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ width: COLUMN_WIDTHS.advisorSegment }}>
                                <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                    {showSegmentoColumn ? _('Segmento') : _('Asesor')}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ width: COLUMN_WIDTHS.status }}>
                                <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                    {_('Estado de la solicitud')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.map((item) => (
                            <TableRow key={item.userNode.id} hover>
                                <GrowerAndAdvisorCells
                                    userNode={item.userNode}
                                    showSegmentoColumn={showSegmentoColumn}
                                    advisorName={advisorNameByNodeId[item.userNode.id]}
                                />
                                <StatusCell item={item} />
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </ModalV2>
    );
};

export default InvitationResultsModal;
